import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { adminAPI, authAPI } from "../services/api";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Create User Modal State
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ fullName: "", email: "", password: "" });

  // Edit Email Modal State
  const [showEditEmail, setShowEditEmail] = useState(false);
  const [editEmailData, setEditEmailData] = useState({ userId: null, currentEmail: "", newEmail: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    if (!user.isAdmin) {
      navigate("/dashboard");
      return;
    }

    fetchAdminData();
  }, [navigate]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      const [usersData, statsData] = await Promise.all([
        adminAPI.getAllUsers(),
        adminAPI.getDashboard()
      ]);

      setUsers(usersData);
      setStats(statsData);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      setError("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await authAPI.register(newUser.fullName, newUser.email, newUser.password);
      alert("User created successfully!");
      setShowCreateUser(false);
      setNewUser({ fullName: "", email: "", password: "" });
      fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create user");
    }
  };

  const handleUpdateUserStatus = async (userId, currentStatus) => {
    try {
      await adminAPI.updateUserStatus(userId, !currentStatus);
      alert("User status updated successfully");
      fetchAdminData();
    } catch (err) {
      alert("Failed to update user status");
    }
  };

  const openEditEmailModal = (userId, currentEmail) => {
    setEditEmailData({ userId, currentEmail, newEmail: currentEmail });
    setShowEditEmail(true);
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(
        `/Admin/users/${editEmailData.userId}/email`,
        { email: editEmailData.newEmail }
      );

      alert(`Email updated successfully!\nOld: ${response.data.oldEmail}\nNew: ${response.data.newEmail}`);
      setShowEditEmail(false);
      setEditEmailData({ userId: null, currentEmail: "", newEmail: "" });
      fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update email");
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  // Agent management
  const promoteToAgent = async (userId) => {
    if (!window.confirm("Promote this user to Agent?")) return;
    
    try {
      await api.put(`/Admin/users/${userId}/promote-to-agent`, {});
      alert("User promoted to Agent successfully!");
      fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to promote user");
    }
  };

  const demoteFromAgent = async (userId) => {
    if (!window.confirm("Demote this agent to regular user?")) return;
    
    try {
      await api.put(`/Admin/users/${userId}/demote-from-agent`, {});
      alert("Agent demoted successfully!");
      fetchAdminData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to demote agent");
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading admin dashboard...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>🛠️ Admin Dashboard</h1>
        <div style={styles.headerButtons}>
          <button onClick={() => navigate("/dashboard")} style={styles.button}>
            User Dashboard
          </button>
          <button onClick={() => navigate("/accounting")} style={{ ...styles.button, ...styles.accountingButton }}>
            📊 Accounting
          </button>
          <button onClick={() => navigate("/fees")} style={{ ...styles.button, ...styles.feeButton }}>
            💰 Fee Setup
          </button>
          <button onClick={() => navigate("/fee-sharing")} style={{ ...styles.button, ...styles.feeSharingButton }}>
            📊 Fee Sharing
          </button>
          <button onClick={() => navigate("/reconciliation-report")} style={{ ...styles.button, ...styles.reconciliationButton }}>
            📑 Reconciliation
          </button>
          <button onClick={() => navigate("/license-management")} style={{ ...styles.button, ...styles.licenseButton }}>
            🔑 License
          </button>
          <button onClick={() => navigate("/settings")} style={{ ...styles.button, ...styles.settingsButton }}>
            ⚙️ Settings
          </button>
          <button onClick={logout} style={{ ...styles.button, ...styles.logoutButton }}>
            Logout
          </button>
          <button onClick={() => navigate("/company-finance")} style={styles.button}>
            💰 Company Finance
          </button>
          <button onClick={() => navigate("/reversals")} style={{ ...styles.button, ...styles.reversalsButton }}>
            🔄 Transaction Reversals
          </button>
          <button onClick={() => navigate("/bank-accounts")} style={styles.button}>
            🏦 Bank Accounts
          </button>
          <button onClick={() => navigate("/integrations")} style={{ ...styles.button, ...styles.integrationsButton }}>
            🔗 Third-Party Integrations
          </button>
          <button onClick={() => navigate("/account-type-admin")} style={{ ...styles.button, ...styles.accountTypeButton }}>
            ⚙️ Account Type Configuration
          </button>
          <button onClick={() => navigate("/admin/email")} style={{ ...styles.button, ...styles.emailButton }}>
            📧 Email Management
          </button>
          <button onClick={() => navigate("/branch-management")} style={{ ...styles.button, backgroundColor: "#6366f1", color: "white" }}>
            🏢 Branch Management
          </button>
          <button onClick={() => navigate("/company-profile")} style={{ ...styles.button, backgroundColor: "#17a2b8", color: "white" }}>
            🏢 Company Profile
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Action Buttons */}
      <div style={styles.actionButtons}>
        <button 
          onClick={() => setShowCreateUser(true)} 
          style={{ ...styles.button, ...styles.createButton }}
        >
          ➕ Create New User
        </button>
        <button 
          onClick={() => navigate("/account-opening")} 
          style={{ ...styles.button, backgroundColor: "#059669", color: "white" }}
        >
          🏦 Account Opening
        </button>
        <button 
          onClick={() => navigate("/pending-kyc")} 
          style={{ ...styles.button, backgroundColor: "#f59e0b", color: "white" }}
        >
          📋 Pending KYC
        </button>
        <button 
          onClick={() => navigate("/kyc-admin")} 
          style={{ ...styles.button, backgroundColor: "#f8f9fa", color: "#333", border: "1px solid #ddd" }}
        >
          🔒 KYC Management
        </button>
        <button 
          onClick={() => navigate("/risk-management")} 
          style={{ ...styles.button, backgroundColor: "#dc3545", color: "white" }}
        >
          🛡️ Risk Management
        </button>
        <button 
          onClick={() => navigate("/role-management")} 
          style={{ ...styles.button, backgroundColor: "#8b5cf6", color: "white" }}
        >
          👥 Role Management
        </button>
        <button 
          onClick={() => navigate("/float-management")} 
          style={{ ...styles.button, backgroundColor: "#0891b2", color: "white" }}
        >
          💵 Agent Float
        </button>
		<button onClick={() => navigate("/merchant-management")} style={{ ...styles.button, backgroundColor: "#10b981", color: "white" }}>
  🏪 Merchant Management
</button>
        <button onClick={() => navigate("/bills-transactions")} style={{ ...styles.button, backgroundColor: "#7c3aed", color: "white" }}>
  💳 Bills Transactions
</button>
        <button onClick={() => navigate("/bill-payment-limits")} style={{ ...styles.button, backgroundColor: "#f59e0b", color: "white" }}>
  ⚙️ Bill Limits
</button>
        <button onClick={() => navigate("/wallet-settings")} style={{ ...styles.button, backgroundColor: "#10b981", color: "white" }}>
  💰 Wallet Settings
</button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <h3>👥 Total Users</h3>
            <p style={styles.statNumber}>{stats.totalUsers}</p>
          </div>
          <div style={styles.statCard}>
            <h3>✅ Active Users</h3>
            <p style={styles.statNumber}>{stats.activeUsers}</p>
          </div>
          <div style={styles.statCard}>
            <h3>💰 Total Balance</h3>
            <p style={styles.statNumber}>₦{stats.totalBalance?.toLocaleString()}</p>
          </div>
          <div style={styles.statCard}>
            <h3>👛 Total Wallets</h3>
            <p style={styles.statNumber}>{stats.totalWallets}</p>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div style={styles.card}>
        <h2 style={styles.sectionTitle}>📋 All Users</h2>
        
        {users.length === 0 ? (
          <p style={styles.noData}>No users found</p>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={styles.tableRow}>
                    <td style={styles.td}>{user.id}</td>
                    <td style={styles.td}>{user.fullName}</td>
                    <td style={styles.td}>
                      {user.email}
                      <button 
                        onClick={() => openEditEmailModal(user.id, user.email)}
                        style={styles.editButton}
                        title="Edit Email"
                      >
                        ✏️
                      </button>
                    </td>
                    <td style={styles.td}>
                      <span style={user.isActive ? styles.badgeActive : styles.badgeInactive}>
                        {user.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {user.isAdmin ? (
                        <span style={{ ...styles.badge, backgroundColor: "#6f42c1" }}>Admin</span>
                      ) : user.isAgent ? (
                        <span style={{ ...styles.badge, backgroundColor: "#fd7e14" }}>Agent</span>
                      ) : (
                        <span style={{ ...styles.badge, backgroundColor: "#6c757d" }}>User</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleUpdateUserStatus(user.id, user.isActive)}
                        style={user.isActive ? styles.deactivateButton : styles.activateButton}
                      >
                        {user.isActive ? "Deactivate" : "Activate"}
                      </button>
                      {!user.isAdmin && (
                        user.isAgent ? (
                          <button
                            onClick={() => demoteFromAgent(user.id)}
                            style={{ ...styles.actionButton, backgroundColor: "#dc3545", marginLeft: "5px" }}
                          >
                            Demote
                          </button>
                        ) : (
                          <button
                            onClick={() => promoteToAgent(user.id)}
                            style={{ ...styles.actionButton, backgroundColor: "#28a745", marginLeft: "5px" }}
                          >
                            Make Agent
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <div style={styles.inputGroup}>
                <label>Full Name</label>
                <input
                  type="text"
                  value={newUser.fullName}
                  onChange={(e) => setNewUser({...newUser, fullName: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label>Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label>Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.modalButtons}>
                <button type="submit" style={styles.button}>Create User</button>
                <button 
                  type="button" 
                  onClick={() => setShowCreateUser(false)} 
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Email Modal */}
      {showEditEmail && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>Update Email Address</h2>
            <form onSubmit={handleUpdateEmail}>
              <div style={styles.inputGroup}>
                <label>Current Email</label>
                <input
                  type="email"
                  value={editEmailData.currentEmail}
                  disabled
                  style={{...styles.input, backgroundColor: "#f0f0f0"}}
                />
              </div>
              <div style={styles.inputGroup}>
                <label>New Email</label>
                <input
                  type="email"
                  value={editEmailData.newEmail}
                  onChange={(e) => setEditEmailData({...editEmailData, newEmail: e.target.value})}
                  required
                  style={styles.input}
                />
              </div>
              <div style={styles.modalButtons}>
                <button type="submit" style={styles.button}>Update Email</button>
                <button 
                  type="button" 
                  onClick={() => setShowEditEmail(false)} 
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
  },
  loading: {
    textAlign: "center",
    marginTop: "100px",
    fontSize: "18px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    flexWrap: "wrap",
    gap: "10px",
  },
  headerButtons: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  button: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#007bff",
    color: "white",
  },
  logoutButton: {
    backgroundColor: "#dc3545",
  },
  accountingButton: {
    backgroundColor: "#17a2b8",
  },
  feeButton: {
    backgroundColor: "#6f42c1",
  },
  feeSharingButton: {
    backgroundColor: "#20c997",
  },
  reconciliationButton: {
    backgroundColor: "#e83e8c",
  },
  licenseButton: {
    backgroundColor: "#6610f2",
  },
  settingsButton: {
    backgroundColor: "#fd7e14",
  },
  reversalsButton: {
    backgroundColor: "#dc2626",
  },
  integrationsButton: {
    backgroundColor: "#8b5cf6",
  },
  accountTypeButton: {
    backgroundColor: "#8b5cf6",
  },
  emailButton: {
    backgroundColor: "#e91e63",
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  createButton: {
    backgroundColor: "#28a745",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "20px",
    marginBottom: "30px",
  },
  statCard: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    textAlign: "center",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: "bold",
    color: "#007bff",
    margin: "10px 0 0 0",
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  sectionTitle: {
    fontSize: "22px",
    marginBottom: "20px",
    color: "#333",
  },
  noData: {
    textAlign: "center",
    color: "#666",
    padding: "20px",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    backgroundColor: "#f8f9fa",
  },
  th: {
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #dee2e6",
    fontWeight: "600",
    color: "#495057",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #dee2e6",
  },
  td: {
    padding: "12px",
    whiteSpace: "nowrap",
  },
  editButton: {
    marginLeft: "8px",
    padding: "2px 6px",
    fontSize: "14px",
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "#007bff",
  },
  badge: {
    display: "inline-block",
    padding: "4px 12px",
    color: "white",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
  },
  badgeActive: {
    display: "inline-block",
    padding: "4px 12px",
    backgroundColor: "#28a745",
    color: "white",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
  },
  badgeInactive: {
    display: "inline-block",
    padding: "4px 12px",
    backgroundColor: "#dc3545",
    color: "white",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "500",
  },
  actionButton: {
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    color: "white",
  },
  activateButton: {
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "500",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    backgroundColor: "#28a745",
    color: "white",
  },
  deactivateButton: {
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "500",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    backgroundColor: "#dc3545",
    color: "white",
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "500px",
  },
  inputGroup: {
    marginBottom: "20px",
  },
  input: {
    width: "100%",
    padding: "10px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
    marginTop: "5px",
  },
  modalButtons: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
  },
  cancelButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "1px solid #ddd",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "white",
    color: "#333",
  },
};
